import { eachMonthOfInterval, endOfMonth, format, startOfYear, subYears, addMonths } from "date-fns";
import { getDealOwner, getFullName, getUserIdsForTeam } from "@revelate/utils";
import type { NestedCompany, NestedCurrency, NestedDeal, NestedTeam } from "@revelate/types";
import type { ForecastPresentationScope, ForecastPresentationSnapshot, ForecastPresentationHistoryDeal } from "@revelate/types/forecast-presentation";
import { buildOutlookHistory } from "@revelate/calc/target-outlook";
import { buildDealValueSegmentMap } from "../lib/dealValueSegments";
import { prepareDealsForRiskAssessment, evaluateOpenDealsForForecast } from "../lib/risk";
import { applyExpectedCloseV2Forecast, type ExpectedCloseV2CompanyForecast } from "../expected-close-v2/localExpectedCloseForecast";
import { getGrowthEfficiencyCreatedDate } from "../state-of-sales-v2/growthEfficiencyData";
import type { OutlookHistoryQualityOptions } from "@revelate/calc/outlook-history-quality";

type HistoryInput = {
  rawDeals: NestedDeal[]; company: NestedCompany; currencies: NestedCurrency[]; teams: NestedTeam[];
  pipelines: Parameters<typeof prepareDealsForRiskAssessment>[1];
  stageTimes: Parameters<typeof prepareDealsForRiskAssessment>[4];
};
const validIso = (value: string | Date | null | undefined) => {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date.toISOString() : null;
};

export function buildForecastHistory(input: HistoryInput): ForecastPresentationHistoryDeal[] {
  const raw = input.rawDeals.map(deal => ({ ...deal, provider_data: typeof deal.provider_data === "string" ? deal.provider_data : JSON.stringify(deal.provider_data) }));
  const prepared = prepareDealsForRiskAssessment(raw, input.pipelines, input.currencies, input.company, input.stageTimes,
    { preserveOverdueDealIds: new Set(raw.map(deal => Number(deal.id))) });
  return prepared.map(deal => {
    const owner = getDealOwner(deal);
    return { id: Number(deal.id), value: deal.value === null || deal.value === undefined || deal.value === "" ? Number.NaN : Number(deal.value), ownerId: owner?.id ?? null,
      pipelineId: String(deal.pipeline ?? ""),
      teamIds: owner ? input.teams.filter(team => getUserIdsForTeam(input.teams, team.id, true).includes(owner.id)).map(team => String(team.id)) : [],
      createdAt: getGrowthEfficiencyCreatedDate(deal)?.toISOString() ?? null,
      closedAt: validIso(deal.closed_at), isWon: Boolean(deal.is_won), isLost: Boolean(deal.is_lost) };
  });
}

export function matchesForecastScope(deal: Pick<ForecastPresentationHistoryDeal, "ownerId" | "pipelineId" | "teamIds">, scope: ForecastPresentationScope) {
  return (!scope.owners.length || (deal.ownerId !== null && scope.owners.includes(deal.ownerId)))
    && (!scope.pipelines.length || scope.pipelines.includes(deal.pipelineId))
    && (!scope.teams.length || deal.teamIds.some(id => scope.teams.includes(id)));
}

/** Records the existing canonical helpers' outputs, with source values frozen alongside them. */
export function buildPresentationSnapshot(input: {
  rawDeals: NestedDeal[]; company: NestedCompany; currencies: NestedCurrency[]; teams: NestedTeam[];
  pipelines: Parameters<typeof prepareDealsForRiskAssessment>[1];
  stageTimes: Parameters<typeof prepareDealsForRiskAssessment>[4];
  artifact: ExpectedCloseV2CompanyForecast; runId: string; modelVersion: string;
  asOf: string; capturedAt: string; currencyCode: string;
  preparedHistory?: ForecastPresentationHistoryDeal[];
}): ForecastPresentationSnapshot {
  const { artifact } = input;
  // Preserve the existing history parser and original CRM creation date. Never
  // substitute the database's synchronization timestamp for opportunity creation.
  const raw = input.rawDeals.map(deal => ({ ...deal, provider_data: typeof deal.provider_data === "string" ? deal.provider_data : JSON.stringify(deal.provider_data) }));
  const teamOwners = input.teams.map(team => ({ id: String(team.id), owners: new Set(getUserIdsForTeam(input.teams, team.id, true)) }));
  const dimensions = (deal: NestedDeal) => {
    const owner = getDealOwner(deal);
    return { ownerId: owner?.id ?? null, pipelineId: String(deal.pipeline ?? ""),
      teamIds: owner ? teamOwners.filter(team => team.owners.has(owner.id)).map(team => team.id) : [] };
  };
  const history = input.preparedHistory ?? buildForecastHistory(input);
  if (history.some(deal => !Number.isFinite(deal.value))) throw new Error("Forecast history contains an unavailable amount.");
  const historyById = new Map(history.map(deal => [deal.id, deal]));
  const supportedIds = new Set(artifact.dealForecasts.map(deal => deal.dealId));
  const terminalIds = new Set(artifact.terminalBuyerOutcomeDeals?.map(deal => deal.dealId));
  const prepared = prepareDealsForRiskAssessment(raw.filter(deal => Number(deal.value) !== 0), input.pipelines,
    input.currencies, input.company, input.stageTimes, { preserveOverdueDealIds: supportedIds });
  const open = evaluateOpenDealsForForecast(prepared.filter(deal => !deal.is_closed && !deal.is_won && !deal.is_lost), input.company.id,
    (input.pipelines as unknown as { results?: unknown[] })?.results ?? []);
  const openById = new Map(open.map(deal => [Number(deal.id), deal]));
  const capturedAt = new Date(input.capturedAt);
  const months = eachMonthOfInterval({ start: startOfYear(subYears(capturedAt, 1)), end: endOfMonth(addMonths(capturedAt, 13)) })
    .map(date => ({ month: format(date, "yyyy-MM"), total_minus_risk: 0, won: 0 }));
  const deals = prepared.map(deal => {
    const id = Number(deal.id);
    const closedMonth = deal.closed_at ? format(new Date(deal.closed_at), "yyyy-MM") : null;
    const evaluated = openById.get(id);
    const contributions = evaluated ? applyExpectedCloseV2Forecast(months, artifact, [evaluated], false)
      .filter(row => Number(row.expected_close_v2) > 0).map(row => [row.month, Number(row.expected_close_v2)] as [string, number])
      : deal.is_won && closedMonth ? [[closedMonth, Number(deal.value)] as [string, number]] : [];
    const owner = getDealOwner(deal);
    return { id, name: String(deal.name), value: Number(deal.value), ...dimensions(deal),
      ownerName: owner ? getFullName(owner) : "Owner not assigned", stage: deal.stage_label ?? null,
      createdAt: historyById.get(id)?.createdAt ?? null, closedAt: deal.closed_at ? new Date(deal.closed_at).toISOString() : null,
      isWon: Boolean(deal.is_won), isLost: Boolean(deal.is_lost), isClosed: Boolean(deal.is_closed),
      expectedClose: evaluated?.adjustedCloseDate ? new Date(evaluated.adjustedCloseDate).toISOString() : null,
      source: deal.is_won || deal.is_lost || deal.is_closed ? "actual" as const : terminalIds.has(id) ? "terminal" as const
        : supportedIds.has(id) ? "v2" as const : "reviewed-fallback" as const,
      monthlyContribution: contributions };
  });
  return { schemaVersion: "forecast-presentation-history-v1", methodVersion: "canonical-v2-with-reviewed-fallback-and-future-v1",
    companyId: input.company.id, runId: input.runId, modelVersion: input.modelVersion, asOf: input.asOf,
    capturedAt: input.capturedAt, currencyCode: input.currencyCode, deals, history };
}

export function projectFuturePipeline(history: ForecastPresentationHistoryDeal[], asOf: Date,
  range: { start: Date; end: Date }, scope: ForecastPresentationScope,
  options?: { boundedHistory: true; recoveredById?: OutlookHistoryQualityOptions["recoveredById"] }) {
  const scoped = history.filter(deal => matchesForecastScope(deal, scope));
  const segments = buildDealValueSegmentMap(scoped);
  return buildOutlookHistory(scoped.map(deal => ({ id: deal.id, value: deal.value,
    createdAt: deal.createdAt ? new Date(deal.createdAt) : null,
    closedAt: deal.closedAt ? new Date(deal.closedAt) : null,
    isWon: deal.isWon, isLost: deal.isLost, dimensions: { segment: segments.get(String(deal.id)) },
  })), asOf, range, options?.boundedHistory
    ? { pipelineById: new Map(scoped.map(deal => [String(deal.id), deal.pipelineId])), recoveredById: options.recoveredById,
      allowRecentCreationFallback: true }
    : undefined);
}

export function snapshotPeriodValue(snapshot: ForecastPresentationSnapshot, months: ReadonlySet<string>, scope: ForecastPresentationScope) {
  return snapshot.deals.filter(deal => matchesForecastScope(deal, scope)).reduce((sum, deal) => sum
    + deal.monthlyContribution.reduce((total, [month, value]) => total + (months.has(month) ? value : 0), 0), 0);
}
